import { Headline, Text } from '@oneaudi/unified-web-components';
import { useAtom, useAtomValue } from 'jotai/index';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { TriggerItemCard } from '~/app/pages/configurator/components/conflict-dialog/components/conflict-items';
import {
	AutomaticChangesSection,
	DecisionGroup,
} from '~/app/pages/configurator/components/conflict-dialog/components/decision-group';
import {
	StyledConflictDialog,
	StyledConflictSection,
	StyledSelectionArea,
} from '~/app/pages/configurator/components/conflict-dialog/styles/conflict-dialog-style';
import { useConflictHandler } from '~/app/pages/configurator/components/conflict-dialog/use-conflict-handler';
import { sortByPriceAndName } from '~/app/pages/configurator/components/conflict-dialog/utils/sort-solution-group';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { Dialog } from '~/components/dialog';
import { useFootnotes } from '~/components/footnotes';
import { MONTHLY_RATE_DIALOG_REMARK } from '~/components/footnotes/constants';
import { ConfiguredCarConflict } from '~/graphql/__generated__/graphql';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';
import { state } from '~/storage';
import { PriceSummaryAndConfirmation } from './components/summary-and-confirmation';

export function ConflictsDialogWrapper({
	open,
	onOpenChange,
	data,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	data: Parameters<typeof useConflictHandler>[0];
}) {
	const currentView = useAtomValue(state.currentView);

	const onDecline = useCallback(
		(element: 'declineButton' | 'closeButton') => {
			onOpenChange(false);
			appTrackingService.trackDeclineConflict({ element });
			element === 'declineButton' && appTrackingService.trackConflictClose();
		},
		[currentView, onOpenChange],
	);

	const onClose = useCallback(() => {
		onDecline('closeButton');
		appTrackingService.trackConflictClose();
		onOpenChange(false);
	}, [onOpenChange]);

	return (
		<Dialog onCloseButtonClick={onClose} open={open}>
			<ConflictsDialog onDecline={() => onDecline('declineButton')} onOpenChange={onOpenChange} data={data} />
		</Dialog>
	);
}

function ConflictsDialog({
	onOpenChange,
	data,
	onDecline,
}: {
	onOpenChange: (open: boolean) => void;
	data: Parameters<typeof useConflictHandler>[0];
	onDecline: () => void;
}) {
	const { i18n } = useI18n();
	const { config } = useMarketConfig();
	const { formatCurrency } = useCurrencyFormatter();
	const vatConfig = useVATConfig();
	const { acceptConflictSolution } = useConfigurator();
	const [isLoadingAccept, setIsLoadingAccept] = useState(false);
	const {
		trigger,
		sign,
		priceChange,
		totalPrice,
		automaticallyAdded,
		automaticallyRemoved,
		decisionRequired,
		acceptedSolutionData,
		changeSelection,
		isLoading,
		isChangingConflictSolutions,
		isItemSelected,
		isItemLoading,
		updatedConflictData,
		trackingSolutionGroups,
	} = useConflictHandler(data);
	const currentView = useAtomValue(state.currentView);
	const wasConflictTracked = useRef<boolean>(false);
	const { addFootnote } = useFootnotes();
	const disclaimer = useAtomValue(state.financingDisclaimerText);
	const [, setLastTrigger] = useAtom(state.lastTrigger);

	if (config.enableFinancingServices && !config.hideMonthlyRates && config.showFinancingDisclaimerInDialogs) {
		addFootnote(MONTHLY_RATE_DIALOG_REMARK, disclaimer);
	}

	setLastTrigger(trigger.texts?.akHeadline || trigger.texts?.akText || null);

	useEffect(() => {
		if (!totalPrice?.price.value || wasConflictTracked.current) return;

		appTrackingService.trackConflictOpen({
			priceChange: data.price?.value || 0,
			newTotalPrice: `${totalPrice?.price.value}`,
			data,
			solutionGroup: trackingSolutionGroups,
		});

		wasConflictTracked.current = true;
	}, [JSON.stringify(data), totalPrice?.price.value, JSON.stringify(trackingSolutionGroups)]);

	// Sort group items by ascending price, when price is similar sort alphabetically
	const { sortedDecisionGroups, sortedAutomaticallyAdded, sortedAutomaticallyRemoved } = useMemo(() => {
		const sortedGroups = decisionRequired.map((group) =>
			group.solutions.map((solution) => solution.feature).sort(sortByPriceAndName),
		);
		const sortedAdded = automaticallyAdded.sort(sortByPriceAndName);
		const sortedRemoved = automaticallyRemoved.sort(sortByPriceAndName);

		return {
			sortedDecisionGroups: sortedGroups,
			sortedAutomaticallyAdded: sortedAdded,
			sortedAutomaticallyRemoved: sortedRemoved,
		};
	}, [automaticallyAdded, automaticallyRemoved, decisionRequired]);

	const onAccept = useCallback(async () => {
		if (acceptedSolutionData.status !== 'success') {
			throw new Error("Can't accept conflict solution when status of acceptedSolution is not success");
		}
		appTrackingService.trackAcceptConflictEvent({
			currentView,
			label: i18n('ngc.conflict.accept'),
			value:
				updatedConflictData && updatedConflictData.price
					? `${sign}${Math.abs(updatedConflictData.price.value)}~${totalPrice?.price.value}`
					: '',
			data: updatedConflictData as ConfiguredCarConflict,
		});
		try {
			setIsLoadingAccept(true);
			await acceptConflictSolution(acceptedSolutionData.data);
			onOpenChange(false);
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoadingAccept(false);
		}
	}, [acceptConflictSolution, acceptedSolutionData, onOpenChange]);

	const onAccordionClick = useCallback(
		(open: boolean, solutionGroup: string, label: string) => {
			appTrackingService.trackConflictAccordionEvent({
				priceChange:
					updatedConflictData && updatedConflictData.price
						? `${sign}${Math.abs(updatedConflictData.price.value)}`
						: '',
				newTotalPrice: `${totalPrice?.price?.value}`,
				selectedSolutionGroup: solutionGroup,
				solutionGroup: trackingSolutionGroups,
				open,
				label,
			});
		},
		[sign, totalPrice?.price?.value, trackingSolutionGroups, updatedConflictData?.price?.value],
	);

	return (
		<StyledConflictDialog>
			<Headline tag={'label'} variant={{ type: 'headline', order: '3', weight: 'bold' }}>
				{i18n('ngc.conflict.adjust')}
			</Headline>

			<StyledConflictSection>
				<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>
					{i18n('ngc.conflict.conflictingOption')}
				</Text>
				<TriggerItemCard item={trigger} />
			</StyledConflictSection>

			{sortedDecisionGroups?.length > 0 && (
				<StyledSelectionArea>
					<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>
						{i18n('ngc.conflict.changesRequired', { count: sortedDecisionGroups.length })}
					</Text>
					<Text variant={{ type: 'copy', order: '2', style: 'normal' }}>
						{i18n('ngc.conflict.changesRequired.subtext')}
					</Text>
					<div className={'border-area'}>
						{sortedDecisionGroups.map((group, groupIndex) => (
							<DecisionGroup
								key={groupIndex}
								// decisionGroupsCount={sortedDecisionGroups.length}
								groupIndex={groupIndex}
								group={group}
								isChangingConflictSolutions={isChangingConflictSolutions}
								isItemLoading={isItemLoading}
								isItemSelected={isItemSelected}
								onItemChecked={changeSelection}
								onAccordionClick={onAccordionClick}
							/>
						))}
					</div>
				</StyledSelectionArea>
			)}

			{automaticallyAdded.length > 0 && (
				<AutomaticChangesSection
					title={i18n('ngc.conflict.added', { count: sortedAutomaticallyAdded.length })}
					subtitle={i18n('ngc.conflict.added.subtext')}
					items={sortedAutomaticallyAdded}
				/>
			)}

			{automaticallyRemoved.length > 0 && (
				<AutomaticChangesSection
					title={i18n('ngc.conflict.removed', { count: sortedAutomaticallyRemoved.length })}
					subtitle={i18n('ngc.conflict.removed.subtext')}
					items={sortedAutomaticallyRemoved}
				/>
			)}

			<PriceSummaryAndConfirmation
				isError={acceptedSolutionData.status === 'error'}
				isLoading={isLoading}
				isLoadingAccept={isLoadingAccept}
				priceChange={priceChange}
				totalPrice={totalPrice ? formatCurrency(totalPrice.price, vatConfig.totalPrice) : null}
				onAccept={onAccept}
				onDecline={onDecline}
			/>
		</StyledConflictDialog>
	);
}
